import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Users, Plus, Settings, X, ChevronRight } from 'lucide-react';
import AccountSidebar from '@/components/AccountSidebar';
import HeaderBar from '@/components/HeaderBar';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function TeamsPage({ token, user, logout }) {
    const [teams, setTeams] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [newTeamName, setNewTeamName] = useState('');
    const [creating, setCreating] = useState(false);
    const navigate = useNavigate();

    useEffect(() => {
        fetchTeams();
    }, []);

    const fetchTeams = async () => {
        try {
            const res = await axios.get(`${API}/teams`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setTeams(res.data);
        } catch (err) {
            toast.error('Failed to load teams');
        } finally {
            setLoading(false);
        }
    };

    const handleCreateTeam = async (e) => {
        e.preventDefault();
        if (!newTeamName.trim()) {
            toast.error('Please enter a team name');
            return;
        }

        setCreating(true);
        try {
            const res = await axios.post(`${API}/teams`, 
                { name: newTeamName.trim(), members: [] },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            toast.success('Team created successfully!');
            setShowCreateModal(false);
            setNewTeamName('');
            fetchTeams();
            
            // Navigate to team settings to add members
            if (res.data.team?.id) {
                navigate(`/team/${res.data.team.id}/settings`);
            }
        } catch (err) {
            const errorMsg = err.response?.data?.detail || 'Failed to create team';
            toast.error(errorMsg);
        } finally {
            setCreating(false);
        }
    };

    if (loading) {
        return (
            <div className="settings-page flex items-center justify-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
            </div>
        );
    }

    return (
        <div className="settings-page">
            {/* Header Bar */}
            <HeaderBar user={user} logout={logout} />

            {/* Settings Header */}
            <div className="settings-header">
                <div className="settings-header-content settings-header-row">
                    <h1 className="settings-title">Teams</h1>
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={() => navigate('/')}
                        className="back-button"
                    >
                        ← Back to dashboard
                    </Button>
                </div>
            </div>

            <div className="settings-container">
                <AccountSidebar token={token} />

                {/* Main Content */}
                <div className="settings-main">
                    <div className="settings-content">
                        {/* Teams Hero Header */}
                        <div className="subscription-hero">
                            <div className="subscription-hero__icon">
                                <Users className="w-5 h-5" />
                            </div>
                            <div className="subscription-hero__text">
                                <h2 className="subscription-hero__title">Teams</h2>
                                <p className="subscription-hero__subtitle">Create teams to collaborate and share access with others.</p>
                            </div>
                            <button
                                onClick={() => setShowCreateModal(true)}
                                className="teams-create-btn"
                            >
                                <Plus className="w-4 h-4" />
                                Create team
                            </button>
                        </div>

                        {teams.length === 0 ? (
                            <div className="teams-empty">
                                <div className="teams-empty__icon">
                                    <Users className="w-7 h-7" />
                                </div>
                                <h3 className="teams-empty__title">No teams yet</h3>
                                <p className="teams-empty__subtitle">
                                    Create a team to collaborate with others on your analytics.
                                </p>
                                <button
                                    onClick={() => setShowCreateModal(true)}
                                    className="teams-create-btn"
                                >
                                    <Plus className="w-4 h-4" />
                                    Create your first team
                                </button>
                            </div>
                        ) : (
                            <ul className="teams-list">
                                {teams.map((team) => {
                                    const canManage = team.role === 'owner' || team.role === 'admin';
                                    const handleClick = () => {
                                        if (canManage) navigate(`/team/${team.id}/settings`);
                                    };
                                    return (
                                        <li
                                            key={team.id}
                                            className={`teams-row ${canManage ? 'teams-row--clickable' : ''}`}
                                            onClick={handleClick}
                                            role={canManage ? 'button' : undefined}
                                            tabIndex={canManage ? 0 : undefined}
                                            onKeyDown={(e) => { if (canManage && (e.key === 'Enter' || e.key === ' ')) handleClick(); }}
                                        >
                                            <div className="teams-row__avatar">
                                                <Users className="w-5 h-5" />
                                            </div>
                                            <div className="teams-row__body">
                                                <div className="teams-row__name">{team.name}</div>
                                                <div className="teams-row__meta">
                                                    {team.member_count} member{team.member_count !== 1 ? 's' : ''}
                                                    <span className="teams-row__sep">·</span>
                                                    <span className={`teams-row__role teams-row__role--${team.role}`}>
                                                        {team.role}
                                                    </span>
                                                </div>
                                            </div>
                                            {canManage ? (
                                                <div className="teams-row__cta">
                                                    <Settings className="w-4 h-4" />
                                                    <span>Settings</span>
                                                    <ChevronRight className="w-4 h-4 teams-row__chevron" />
                                                </div>
                                            ) : (
                                                <span className="teams-row__view-only">View only</span>
                                            )}
                                        </li>
                                    );
                                })}
                            </ul>
                        )}
                    </div>
                </div>
            </div>

            {/* Create Team Modal */}
            {showCreateModal && (
                <div className="teams-modal-overlay" onClick={(e) => { if (e.target === e.currentTarget) setShowCreateModal(false); }}>
                    <div className="teams-modal">
                        <div className="teams-modal__header">
                            <div className="teams-modal__title-block">
                                <div className="teams-modal__icon">
                                    <Users className="w-4 h-4" />
                                </div>
                                <h2 className="teams-modal__title">Create new team</h2>
                            </div>
                            <button
                                onClick={() => setShowCreateModal(false)}
                                className="teams-modal__close"
                                aria-label="Close"
                            >
                                <X className="w-4 h-4" />
                            </button>
                        </div>
                        <form onSubmit={handleCreateTeam} className="teams-modal__form">
                            <div className="form-group">
                                <label className="form-label">Team name</label>
                                <Input
                                    type="text"
                                    placeholder="Enter team name"
                                    value={newTeamName}
                                    onChange={(e) => setNewTeamName(e.target.value)}
                                    autoFocus
                                    className="teams-modal__input"
                                />
                            </div>
                            <p className="teams-modal__help">
                                After creating the team, you can invite members from the team settings page.
                            </p>
                            <div className="teams-modal__footer">
                                <Button
                                    type="button"
                                    variant="outline"
                                    onClick={() => setShowCreateModal(false)}
                                >
                                    Cancel
                                </Button>
                                <button type="submit" disabled={creating} className="pref-submit-btn">
                                    {creating ? 'Creating...' : 'Create team'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
}
